import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowRight, CheckCircle2, Store, CreditCard, BarChart3,
  Clock, Users, ShieldCheck, ChevronDown, ChevronUp,
  Percent, LayoutDashboard, Wallet, Zap, Phone, Mail, User
} from 'lucide-react';

const tradeTypes = [
  'Restaurant',
  'Boulangerie',
  'Boucherie',
  'Primeur',
  'Fromagerie',
  'Poissonnerie',
  'Traiteur',
  'Autre',
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LeadForm({ variant = 'light' }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', trade: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.trade) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      await fetch(`${API}/leads/partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
    } catch {
      // Continue even if lead save fails
    }

    // Redirect to signup with pre-filled data
    const params = new URLSearchParams({
      name: form.name,
      email: form.email,
      phone: form.phone,
      trade: form.trade,
      offer: '15j-gratuit',
    });
    navigate(`/restaurant-signup?${params.toString()}`);
  };

  const isDark = variant === 'dark';

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="partner-lead-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="relative">
          <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <Input
            type="text"
            placeholder="Nom du commerce *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={`pl-10 h-12 rounded-xl ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' : 'bg-white border-gray-200'}`}
            data-testid="lead-name"
            required
          />
        </div>
        <div className="relative">
          <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <Input
            type="email"
            placeholder="Email *"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={`pl-10 h-12 rounded-xl ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' : 'bg-white border-gray-200'}`}
            data-testid="lead-email"
            required
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="relative">
          <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <Input
            type="tel"
            placeholder="Téléphone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className={`pl-10 h-12 rounded-xl ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' : 'bg-white border-gray-200'}`}
            data-testid="lead-phone"
          />
        </div>
        <select
          value={form.trade}
          onChange={(e) => setForm({ ...form, trade: e.target.value })}
          className={`h-12 px-4 rounded-xl border focus:outline-none focus:ring-2 focus:ring-emerald-500 ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
          data-testid="lead-trade"
          required
        >
          <option value="">Type de commerce *</option>
          {tradeTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <Button
        type="submit"
        disabled={submitting}
        className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 rounded-xl font-semibold text-base"
        data-testid="lead-submit"
      >
        {submitting ? 'Redirection...' : 'Créer ma boutique — 0% pendant 15 jours'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
      <p className={`text-xs text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        Sans engagement. Configuration en 15 minutes.
      </p>
    </form>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
        data-testid={`faq-${q.slice(0, 20).replace(/\s/g, '-')}`}
      >
        <span className="font-medium text-gray-900 pr-4">{q}</span>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />}
      </button>
      {open && <p className="pb-4 text-gray-600 text-sm leading-relaxed">{a}</p>}
    </div>
  );
}

export default function DevenirPartenaire() {
  return (
    <div className="min-h-screen bg-white">
      {/* Minimal Nav */}
      <header className="border-b border-gray-100 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="LEMO" className="w-14 h-14 -my-2 object-contain" />
            <span className="font-bold text-xl text-gray-900">LEMO</span>
          </a>
          <a
            href="#formulaire"
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
          >
            Commencer gratuitement
          </a>
        </div>
      </header>

      {/* ==================== HERO ==================== */}
      <section className="py-14 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-medium mb-5">
                <Zap className="w-4 h-4" />
                0% de commission pendant 15 jours
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight leading-[1.1]">
                Votre click & collect.<br />
                <span className="text-emerald-500">Prêt en 15 minutes.</span>
              </h1>
              <p className="mt-5 text-lg text-gray-600 leading-relaxed max-w-lg">
                Vos clients commandent en ligne, paient à l'avance, et passent chercher sans attendre. Vous préparez sereinement.
              </p>

              {/* Key points */}
              <ul className="mt-6 space-y-2.5">
                {[
                  'Seulement 5% de commission (après 15 jours offerts)',
                  'Sans engagement, résiliable à tout moment',
                  'Zéro compétence technique requise',
                  'Paiement en ligne sécurisé via Stripe',
                ].map((point) => (
                  <li key={point} className="flex items-start gap-2.5 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Lead Form */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 lg:p-8" id="formulaire">
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                Lancez votre click & collect
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                Remplissez ce formulaire, on s'occupe du reste.
              </p>
              <LeadForm variant="light" />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== BENEFITS ==================== */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3">
            Ce que LEMO change pour vous
          </h2>
          <p className="text-gray-500 text-center mb-10 max-w-xl mx-auto">
            Concrètement, voici ce que vous gagnez dès le premier jour.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Clock,
                title: 'Moins d\'attente en caisse',
                desc: 'Vos clients commandent et paient avant de venir. Plus de file d\'attente, plus de stress au comptoir.',
              },
              {
                icon: Wallet,
                title: 'Paiement à l\'avance',
                desc: 'Fini les no-shows. Le client paie en ligne, vous préparez uniquement ce qui est confirmé.',
              },
              {
                icon: BarChart3,
                title: 'Plus de commandes',
                desc: 'Les clients qui n\'ont pas le temps de faire la queue commandent enfin chez vous.',
              },
              {
                icon: LayoutDashboard,
                title: 'Un tableau de bord simple',
                desc: 'Gérez vos produits, horaires et commandes depuis une seule interface. Pas besoin d\'être développeur.',
              },
              {
                icon: ShieldCheck,
                title: 'Paiement sécurisé Stripe',
                desc: 'Les transactions sont gérées par Stripe, leader mondial du paiement en ligne. Vos fonds sont versés directement.',
              },
              {
                icon: Users,
                title: 'Fidélisez vos clients',
                desc: 'Cartes de fidélité, promotions automatiques, pré-commandes : des outils pour faire revenir vos clients.',
              },
            ].map((b) => (
              <div key={b.title} className="p-5 rounded-2xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/30 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
                  <b.icon className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{b.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">
            3 étapes. 15 minutes. C'est tout.
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Inscrivez-vous', desc: 'Remplissez le formulaire avec les infos de votre commerce. Aucun document requis pour démarrer.' },
              { step: '2', title: 'Ajoutez vos produits', desc: 'Créez votre menu ou catalogue avec photos, prix et descriptions. Aussi simple que poster sur Instagram.' },
              { step: '3', title: 'Recevez des commandes', desc: 'Vos clients commandent en ligne. Vous recevez une notification, vous préparez, ils passent chercher.' },
            ].map((item) => (
              <div key={item.step} className="relative bg-white rounded-2xl p-6 border border-gray-100">
                <div className="w-10 h-10 rounded-full bg-emerald-500 text-white font-bold text-lg flex items-center justify-center mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== TRADES ==================== */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3">
            LEMO s'adapte à votre métier
          </h2>
          <p className="text-gray-500 text-center mb-10 max-w-xl mx-auto">
            Quel que soit votre commerce de proximité, LEMO est fait pour vous.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { name: 'Restaurant', desc: 'Commandes à emporter, midi express', img: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=300&fit=crop' },
              { name: 'Boulangerie', desc: 'Pains, viennoiseries sur commande', img: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=400&h=300&fit=crop' },
              { name: 'Boucherie', desc: 'Pièces préparées à l\'avance', img: 'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=400&h=300&fit=crop' },
              { name: 'Primeur', desc: 'Paniers de fruits & légumes', img: 'https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=400&h=300&fit=crop' },
              { name: 'Fromagerie', desc: 'Plateaux et assortiments', img: 'https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?w=400&h=300&fit=crop' },
              { name: 'Poissonnerie', desc: 'Commandes fraîches du jour', img: 'https://images.unsplash.com/photo-1764813824645-e5dc9e49d8cb?w=400&h=300&fit=crop' },
              { name: 'Traiteur', desc: 'Événements et pré-commandes', img: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=400&h=300&fit=crop' },
              { name: 'Et plus encore...', desc: 'Tout commerce de proximité', img: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&h=300&fit=crop' },
            ].map((trade) => (
              <div key={trade.name} className="relative rounded-2xl overflow-hidden aspect-[4/3] group">
                <img src={trade.img} alt={trade.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <h3 className="text-white font-bold text-sm sm:text-base">{trade.name}</h3>
                  <p className="text-white/70 text-xs mt-0.5">{trade.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== PRICING ==================== */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">
            Un tarif simple et transparent
          </h2>
          <div className="bg-white rounded-2xl border-2 border-emerald-500 p-8 text-center">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              <Zap className="w-4 h-4" />
              Offre de lancement
            </div>
            <div className="mb-2">
              <span className="text-5xl font-extrabold text-gray-900">0%</span>
              <span className="text-gray-500 ml-2">pendant 15 jours</span>
            </div>
            <p className="text-gray-500 mb-6">
              Puis <span className="font-semibold text-gray-900">5% de commission</span> sur les paiements en ligne uniquement
            </p>
            <div className="grid sm:grid-cols-2 gap-3 text-left max-w-md mx-auto mb-6">
              {[
                'Sans abonnement mensuel',
                'Sans engagement',
                'Sans frais cachés',
                'Résiliable à tout moment',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-gray-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  {item}
                </div>
              ))}
            </div>
            <a
              href="#formulaire"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-full font-semibold transition-colors"
            >
              Commencer gratuitement
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* ==================== TESTIMONIAL ==================== */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gray-50 rounded-2xl p-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Store className="w-7 h-7 text-emerald-600" />
            </div>
            <blockquote className="text-lg sm:text-xl text-gray-900 font-medium leading-relaxed mb-4">
              "J'ai réduit ma file d'attente et augmenté mon chiffre d'affaires de 16% !"
            </blockquote>
            <p className="text-emerald-600 font-semibold">Marie</p>
            <p className="text-gray-500 text-sm">Restaurant "Les Petites Natures"</p>
          </div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section className="py-16 bg-gray-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">
            Questions fréquentes
          </h2>
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <FAQItem
              q="Combien ça coûte ?"
              a="0% de commission pendant vos 15 premiers jours. Ensuite, seulement 5% de commission sur les paiements en ligne. Pas d'abonnement, pas de frais cachés, pas d'engagement."
            />
            <FAQItem
              q="Combien de temps pour être opérationnel ?"
              a="15 minutes. Vous créez votre compte, ajoutez vos produits avec photos et prix, et c'est en ligne. Pas besoin de compétences techniques."
            />
            <FAQItem
              q="Comment je reçois mes paiements ?"
              a="Via Stripe, le leader mondial du paiement en ligne. Les fonds sont versés directement sur votre compte bancaire, automatiquement."
            />
            <FAQItem
              q="Mes clients peuvent-ils payer au comptoir ?"
              a="Oui. Vos clients peuvent choisir de payer en ligne ou au comptoir lors du retrait. Vous décidez quels modes de paiement accepter."
            />
            <FAQItem
              q="Est-ce que je peux résilier ?"
              a="Oui, à tout moment. Aucun engagement, aucune durée minimum. Vous pouvez désactiver votre boutique quand vous le souhaitez."
            />
            <FAQItem
              q="Quels types de commerces peuvent utiliser LEMO ?"
              a="Restaurants, boulangeries, boucheries, primeurs, fromageries, poissonneries, traiteurs... Tous les commerces de proximité qui vendent des produits à emporter."
            />
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="py-16 lg:py-20 bg-gray-900 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
            Prêt à lancer votre click & collect ?
          </h2>
          <p className="text-gray-400 mb-8 max-w-lg mx-auto">
            Rejoignez les commerçants qui simplifient leurs commandes et augmentent leur chiffre d'affaires.
          </p>
          <div className="max-w-lg mx-auto">
            <LeadForm variant="dark" />
          </div>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="bg-gray-950 text-gray-500 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm">
          <span>&copy; {new Date().getFullYear()} LEMO. Tous droits réservés.</span>
          <div className="flex gap-4">
            <a href="/cgu" className="hover:text-white transition-colors">CGU</a>
            <a href="/cgv" className="hover:text-white transition-colors">CGV</a>
            <a href="/politique-de-confidentialite" className="hover:text-white transition-colors">Confidentialité</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
